import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'autosuggest-highlight/parse';

import { Autocomplete, Box, Card, Grid, Stack, TextField, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import config from 'config';

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const sessionToken = { current: null };

const GoogleMapPlaces = (props) => {
  const { onAddressChange, selectedValue } = props;

  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const loaded = useRef(false);

  const searchRef = useRef(null);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      window.__initGoogleMaps = () => {};

      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places&loading=async&callback=__initGoogleMaps`,
        document.querySelector('head'),
        'google-maps'
      );
    }

    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      debounce(async (request, callback) => {
        if (!window.google?.maps?.places?.AutocompleteSuggestion) {
          return callback([]);
        }

        try {
          const newRequest = {
            input: request.input,
            sessionToken: request.sessionToken,
          };

          if (request.componentRestrictions?.country) {
            newRequest.includedRegionCodes = Array.isArray(request.componentRestrictions.country)
              ? request.componentRestrictions.country
              : [request.componentRestrictions.country];
          }

          const { suggestions } =
            await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(newRequest);

          const legacyResults = suggestions
            .map((suggestion) => {
              const p = suggestion.placePrediction;
              if (!p) return null;

              const legacyMatches = (p.mainText?.matches || []).map((m) => {
                const offset = m.startOffset !== undefined ? m.startOffset : m.offset || 0;
                const length = m.endOffset !== undefined ? m.endOffset - offset : m.length || 0;
                return { offset, length };
              });

              return {
                description: p.text?.text || '',
                place_id: p.placeId,
                structured_formatting: {
                  main_text: p.mainText?.text || '',
                  main_text_matched_substrings: legacyMatches,
                  secondary_text: p.secondaryText?.text || '',
                },
              };
            })
            .filter(Boolean);

          callback(legacyResults);
        } catch (error) {
          console.error('Error fetching autocomplete suggestions:', error);
          callback([]);
        }
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    if (window.google) {
      if (!sessionToken.current) {
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: config.googleComponentRestrictions,
        sessionToken: sessionToken.current,
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const getPlaceDetails = async (value) => {
    if (!value?.place_id) {
      return onAddressChange('');
    }

    try {
      const place = new window.google.maps.places.Place({
        id: value.place_id,
      });

      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'location', 'addressComponents'],
      });

      const legacyResultsFormat = {
        place_id: place.id,
        name: place.displayName,
        formatted_address: place.formattedAddress,
        geometry: {
          location: place.location,
        },
        address_components: place.addressComponents?.map((component) => ({
          long_name: component.longText,
          short_name: component.shortText,
          types: component.types,
        })),
      };

      onAddressChange(value, legacyResultsFormat);
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  return selectedValue?.placeId !== '' ? (
    <Card
      variant="outlined"
      sx={{ py: 0.85, px: 1 }}
    >
      <Stack direction="row">
        <Grid
          container
          alignItems="center"
        >
          <Grid
            item
            sx={{ display: 'flex', width: 35, ml: 0.5 }}
          >
            <LocationOnIcon sx={{ color: 'text.secondary' }} />
          </Grid>
          <Grid
            item
            sx={{
              width: 'calc(100% - 44px)',
              wordWrap: 'break-word',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              {selectedValue?.address || ''}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}
            >
              {selectedValue?.suburb || ''} {selectedValue?.addressState || ''}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          item
          sx={{ alignSelf: 'center' }}
        >
          <CloseOutlinedIcon
            fontSize="xsmall"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              setValue(null);
              onAddressChange('');
              searchRef?.current?.focus();
            }}
          />
        </Grid>
      </Stack>
    </Card>
  ) : (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      fullWidth
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No locations"
      size="small"
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);

        getPlaceDetails(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      ref={searchRef}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search location"
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const matches = option.structured_formatting.main_text_matched_substrings || [];
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );
        const { key, ...rest } = props;

        return (
          <li
            key={key}
            {...rest}
            style={{ padding: '6px 16px', minHeight: 'auto' }}
          >
            <Grid
              container
              alignItems="center"
              wrap="nowrap"
            >
              <Grid
                item
                sx={{ display: 'flex', pr: 1.3 }}
              >
                <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Grid>
              <Grid
                item
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ lineHeight: 1.2, mb: 0.3 }}
                >
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                    >
                      {part.text}
                    </Box>
                  ))}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ lineHeight: 1.2 }}
                >
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

GoogleMapPlaces.propTypes = {
  onAddressChange: PropTypes.func,
  selectedValue: PropTypes.object,
};

export default GoogleMapPlaces;
